import type { ReactNode } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import {
  ackAlert,
  detectAnomalies,
  detectFreshness,
  getBacktestAvailableDates,
  getDriftReport,
  getLLMBudget,
  getLLMCosts,
  listAlerts,
  listBacktestRuns,
  runBacktestPnl,
  runWatchdogs,
  triggerBacktest,
  type AlertOut,
  type BacktestPnlReport,
  type DriftReport,
  type LLMCostSummary,
  type WatchdogResult,
} from "@/lib/api";
import { Plot, PLOTLY_CONFIG_BASE } from "@/components/charts/plot";
import { fmtCompact, fmtDateTime, fmtNumber, fmtPercent, relativeAge } from "@/lib/format";
import { Panel } from "@/components/ui/Panel";
import { Stat } from "@/components/ui/Stat";
import { Pill } from "@/components/ui/Pill";
import { Button } from "@/components/ui/Button";
import { Label, NumberInput, Select, TextInput } from "@/components/ui/Input";
import { DataTable } from "@/components/ui/Table";
import { Tabs } from "@/components/ui/Tabs";
import { LoadingState, EmptyState, ErrorState } from "@/components/ui/States";
import { useActiveWeightVector } from "@/hooks/useActiveWeightVector";

type TabKey = "drift" | "freshness" | "anomalies" | "alerts" | "backtest" | "watchdogs" | "llm_costs";

export function GovernancePage() {
  const [tab, setTab] = useState<TabKey>("drift");
  const { activeId } = useActiveWeightVector();

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Governance</h1>
          <p className="text-2xs text-foreground-muted">
            Drift · stale-data · anomaly · alert review.
          </p>
        </div>
        <Tabs<TabKey>
          value={tab}
          onChange={setTab}
          options={[
            { value: "drift", label: "Drift" },
            { value: "freshness", label: "Freshness" },
            { value: "anomalies", label: "Anomalies" },
            { value: "alerts", label: "Alerts" },
            { value: "backtest", label: "Backtest" },
            { value: "watchdogs", label: "Watchdogs" },
            { value: "llm_costs", label: "LLM Costs" },
          ]}
        />
      </header>

      {tab === "drift" && <DriftSection activeWeightVectorId={activeId} />}
      {tab === "freshness" && <FreshnessSection />}
      {tab === "anomalies" && <AnomalySection activeWeightVectorId={activeId} />}
      {tab === "alerts" && <AlertsSection />}
      {tab === "backtest" && <BacktestSection activeWeightVectorId={activeId} />}
      {tab === "watchdogs" && <WatchdogsSection activeWeightVectorId={activeId} />}
      {tab === "llm_costs" && <LLMCostsSection />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drift
// ---------------------------------------------------------------------------

function DriftSection({ activeWeightVectorId }: { activeWeightVectorId: number | undefined }) {
  const [refDate, setRefDate] = useState("");
  const [curDate, setCurDate] = useState("");
  const [report, setReport] = useState<DriftReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () =>
      getDriftReport({
        weight_vector_id: activeWeightVectorId ?? 1,
        reference_as_of: refDate,
        current_as_of: curDate,
      }),
    onSuccess: (data) => {
      setReport(data);
      setError(null);
    },
    onError: (err: unknown) => {
      setReport(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  return (
    <Panel
      title="Distribution drift (PSI)"
      description="Population Stability Index across composite + factor z-scores. <0.10 stable · 0.10–0.25 moderate · >0.25 material."
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label>Reference as-of</Label>
          <TextInput
            type="date"
            value={refDate}
            onChange={(e) => setRefDate(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label>Current as-of</Label>
          <TextInput
            type="date"
            value={curDate}
            onChange={(e) => setCurDate(e.target.value)}
          />
        </div>
        <div className="flex items-end">
          <Button
            variant="primary"
            size="sm"
            disabled={!refDate || !curDate}
            loading={run.isPending}
            onClick={() => run.mutate()}
          >
            Run drift report
          </Button>
        </div>
      </div>

      {error && (
        <div className="pt-3">
          <ErrorState detail={error} />
        </div>
      )}

      {report && (
        <div className="pt-4 space-y-3">
          <div className="grid gap-2 sm:grid-cols-3">
            <Panel><Stat label="Sections" value={report.sections.length} /></Panel>
            <Panel>
              <Stat
                label="Material"
                value={report.material_count}
                tone={report.material_count > 0 ? "bad" : "default"}
              />
            </Panel>
            <Panel>
              <Stat
                label="Moderate"
                value={report.moderate_count}
                tone={report.moderate_count > 0 ? "warn" : "default"}
              />
            </Panel>
          </div>

          <DataTable
            dense
            rows={report.sections}
            rowKey={(r) => r.name}
            columns={[
              {
                key: "name",
                header: "Section",
                cell: (r) => <code className="text-2xs">{r.name}</code>,
              },
              {
                key: "psi",
                header: "PSI",
                cell: (r) => (Number.isFinite(r.psi) ? r.psi.toFixed(3) : "n/a"),
                align: "right",
              },
              {
                key: "severity",
                header: "Severity",
                cell: (r) => (
                  <Pill
                    tone={
                      r.severity === "material"
                        ? "bad"
                        : r.severity === "moderate"
                          ? "warn"
                          : "good"
                    }
                  >
                    {r.severity}
                  </Pill>
                ),
              },
              {
                key: "n_ref",
                header: "n ref",
                cell: (r) => r.n_reference,
                align: "right",
              },
              {
                key: "n_cur",
                header: "n cur",
                cell: (r) => r.n_current,
                align: "right",
              },
            ]}
          />
        </div>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Freshness
// ---------------------------------------------------------------------------

function FreshnessSection() {
  const [emit, setEmit] = useState(false);

  const run = useMutation({
    mutationFn: () => detectFreshness({ emit_alerts: emit }),
  });

  return (
    <Panel
      title="Stale-data findings"
      description="Tier-2 inputs older than per-domain thresholds."
      action={
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-2xs text-foreground-muted">
            <input
              type="checkbox"
              checked={emit}
              onChange={(e) => setEmit(e.target.checked)}
            />
            emit alerts
          </label>
          <Button
            variant="primary"
            size="sm"
            loading={run.isPending}
            onClick={() => run.mutate()}
          >
            Detect
          </Button>
        </div>
      }
    >
      {run.data ? (
        <>
          <p className="pb-3 text-2xs text-foreground-muted">
            {run.data.findings.length} findings · {run.data.alerts_emitted} alerts
            written
          </p>
          {run.data.findings.length === 0 ? (
            <EmptyState title="No stale data" description="All Tier-2 facts are within thresholds." />
          ) : (
            <DataTable
              dense
              rows={run.data.findings}
              rowKey={(f) => `${f.domain}:${f.security_id}`}
              columns={[
                { key: "domain", header: "Domain", cell: (f) => f.domain },
                { key: "ticker", header: "Ticker", cell: (f) => f.ticker ?? f.security_id },
                {
                  key: "last_seen",
                  header: "Last seen",
                  cell: (f) => f.last_seen ?? "never",
                },
                {
                  key: "age",
                  header: "Age",
                  cell: (f) =>
                    f.age_days === null ? "—" : `${f.age_days}d`,
                  align: "right",
                },
                {
                  key: "threshold",
                  header: "Threshold",
                  cell: (f) => `${f.threshold_days}d`,
                  align: "right",
                },
              ]}
            />
          )}
        </>
      ) : run.isPending ? (
        <LoadingState />
      ) : (
        <EmptyState
          title="Click Detect to scan"
          description="Walks prices, fundamentals, estimates, and filings."
        />
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Anomalies
// ---------------------------------------------------------------------------

function AnomalySection({ activeWeightVectorId }: { activeWeightVectorId: number | undefined }) {
  const [asOf, setAsOf] = useState("");
  const [sigma, setSigma] = useState(3.0);
  const [absThreshold, setAbsThreshold] = useState(10);
  const [emit, setEmit] = useState(false);

  const run = useMutation({
    mutationFn: () =>
      detectAnomalies({
        weight_vector_id: activeWeightVectorId ?? 1,
        as_of: asOf,
        sigma_threshold: sigma,
        min_abs_threshold: absThreshold,
        emit_alerts: emit,
      }),
  });

  return (
    <Panel
      title="Score anomalies"
      description="Securities whose composite move exceeds the universe-agnostic absolute floor or the per-security σ threshold."
    >
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="space-y-1">
          <Label>As-of</Label>
          <TextInput
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label>σ threshold</Label>
          <NumberInput
            min={1}
            max={10}
            step={0.5}
            value={sigma}
            onChange={(e) => setSigma(Number(e.target.value))}
          />
        </div>
        <div className="space-y-1">
          <Label>|Δ| floor</Label>
          <NumberInput
            min={1}
            max={50}
            step={1}
            value={absThreshold}
            onChange={(e) => setAbsThreshold(Number(e.target.value))}
          />
        </div>
        <div className="flex items-end gap-2">
          <label className="flex items-center gap-1 text-2xs text-foreground-muted">
            <input
              type="checkbox"
              checked={emit}
              onChange={(e) => setEmit(e.target.checked)}
            />
            emit alerts
          </label>
          <Button
            variant="primary"
            size="sm"
            disabled={!asOf}
            loading={run.isPending}
            onClick={() => run.mutate()}
          >
            Detect
          </Button>
        </div>
      </div>

      <div className="pt-4">
        {run.isPending && <LoadingState />}
        {run.data && run.data.anomalies.length === 0 && (
          <EmptyState title="No anomalies" />
        )}
        {run.data && run.data.anomalies.length > 0 && (
          <DataTable
            dense
            rows={run.data.anomalies}
            rowKey={(r) => r.security_id}
            columns={[
              { key: "ticker", header: "Ticker", cell: (r) => r.ticker ?? r.security_id },
              {
                key: "delta",
                header: "Δ",
                cell: (r) => (
                  <span className={r.delta >= 0 ? "text-success" : "text-danger"}>
                    {r.delta >= 0 ? "+" : ""}
                    {r.delta.toFixed(1)}
                  </span>
                ),
                align: "right",
              },
              {
                key: "z",
                header: "z-Δ",
                cell: (r) => (r.z_delta === null ? "n/a" : r.z_delta.toFixed(2)),
                align: "right",
              },
              {
                key: "from",
                header: "From",
                cell: (r) => r.prior_composite.toFixed(1),
                align: "right",
              },
              {
                key: "to",
                header: "To",
                cell: (r) => r.composite.toFixed(1),
                align: "right",
              },
              { key: "as_of", header: "As of", cell: (r) => r.as_of },
            ]}
          />
        )}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Alerts review
// ---------------------------------------------------------------------------

function AlertsSection() {
  const queryClient = useQueryClient();
  const [severity, setSeverity] = useState<string>("");
  const [onlyOpen, setOnlyOpen] = useState(true);
  const alerts = useQuery({
    queryKey: ["governance", "alerts", severity, onlyOpen],
    queryFn: () =>
      listAlerts({
        severity: (severity || undefined) as
          | "info"
          | "warning"
          | "critical"
          | undefined,
        only_open: onlyOpen,
        limit: 200,
      }),
  });
  const ack = useMutation({
    mutationFn: (id: number) => ackAlert(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["governance", "alerts"] }),
  });

  return (
    <Panel
      title="Alerts"
      description="Stale-data, freshness, anomaly, thesis-flip events. Acknowledge to clear."
      action={
        <div className="flex items-center gap-2">
          <Select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="w-32"
          >
            <option value="">All severities</option>
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="critical">critical</option>
          </Select>
          <label className="flex items-center gap-1 text-2xs text-foreground-muted">
            <input
              type="checkbox"
              checked={onlyOpen}
              onChange={(e) => setOnlyOpen(e.target.checked)}
            />
            only open
          </label>
        </div>
      }
    >
      {alerts.isLoading && <LoadingState />}
      {alerts.data && alerts.data.length === 0 && (
        <EmptyState title="No alerts" description="Pipelines are healthy." />
      )}
      {alerts.data && alerts.data.length > 0 && (
        <DataTable
          dense
          rows={alerts.data}
          rowKey={(r) => r.id}
          columns={[
            {
              key: "severity",
              header: "Severity",
              cell: (r) => <SeverityChip a={r} />,
            },
            { key: "type", header: "Type", cell: (r) => r.alert_type },
            { key: "title", header: "Title", cell: (r) => r.title },
            {
              key: "triggered",
              header: "Triggered",
              cell: (r) => (
                <span title={fmtDateTime(r.triggered_at)}>
                  {relativeAge(r.triggered_at)}
                </span>
              ),
            },
            {
              key: "ack",
              header: "",
              cell: (r) =>
                r.acknowledged_at ? (
                  <Pill tone="muted">acked</Pill>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => ack.mutate(r.id)}
                  >
                    Ack
                  </Button>
                ),
              align: "right",
            },
          ]}
        />
      )}
    </Panel>
  );
}

function SeverityChip({ a }: { a: AlertOut }) {
  const tone =
    a.severity === "critical" ? "bad" : a.severity === "warning" ? "warn" : "muted";
  return (
    <Pill tone={tone}>
      <ShieldAlert size={10} className="mr-1" />
      {a.severity}
    </Pill>
  );
}

// ---------------------------------------------------------------------------
// Backtest tab
// ---------------------------------------------------------------------------

function BacktestSection({
  activeWeightVectorId,
}: {
  activeWeightVectorId: number | undefined;
}) {
  const qc = useQueryClient();
  const [view, setView] = useState<"stability" | "pnl">("stability");

  const runs = useQuery({
    queryKey: ["backtest-runs", activeWeightVectorId],
    queryFn: () =>
      listBacktestRuns({ weight_vector_id: activeWeightVectorId, limit: 10 }),
    enabled: activeWeightVectorId !== undefined,
  });

  const trigger = useMutation({
    mutationFn: () =>
      triggerBacktest({
        weight_vector_id: activeWeightVectorId!,
        lookback_days: 365,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["backtest-runs"] });
    },
  });

  const latestRun = runs.data?.[0];
  const latestStabilities = latestRun?.output_summary?.backtest?.stabilities ?? [];
  const lastStab = latestStabilities[latestStabilities.length - 1];

  return (
    <div className="space-y-4">
      {/* Sub-view toggle: ranking stability (existing) vs P&L simulation (G8). */}
      <div className="flex gap-1.5">
        {(["stability", "pnl"] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={`rounded-full border px-3 py-0.5 text-2xs font-medium transition ${
              view === v
                ? "border-accent bg-accent-subtle text-accent-strong"
                : "border-border bg-surface-base text-foreground-secondary hover:bg-surface-subtle"
            }`}
          >
            {v === "stability" ? "Ranking stability" : "P&L simulation"}
          </button>
        ))}
      </div>

      {view === "pnl" && <PnlBacktestView activeWeightVectorId={activeWeightVectorId} />}

      {view === "stability" && (
        <>
      <Panel
        title="Strategy backtest"
        description="Re-runs the scoring pipeline at each stored as-of date over the last 12 months and measures ranking stability period-over-period. Results are persisted — click 'Run now' to refresh."
        action={
          <Button
            variant="primary"
            size="sm"
            loading={trigger.isPending}
            disabled={activeWeightVectorId === undefined}
            onClick={() => trigger.mutate()}
          >
            Run now
          </Button>
        }
      >
        {!activeWeightVectorId && (
          <EmptyState
            title="No active weight vector"
            description="Activate a weight vector on the Settings page first."
          />
        )}
        {activeWeightVectorId && (
          <>
            {trigger.isError && (
              <p className="mb-2 text-xs text-danger">
                {(trigger.error as Error).message}
              </p>
            )}
            {/* Latest-run KPI strip */}
            {lastStab && (
              <div className="mb-4 grid gap-3 sm:grid-cols-3">
                <SparkStat
                  label="Spearman (last period)"
                  value={lastStab.spearman?.toFixed(3) ?? "—"}
                  hint="1 = perfect rank stability"
                  tone={
                    lastStab.spearman == null
                      ? "muted"
                      : lastStab.spearman >= 0.8
                        ? "good"
                        : lastStab.spearman >= 0.5
                          ? "warn"
                          : "bad"
                  }
                />
                <SparkStat
                  label="Top-10 retention"
                  value={
                    lastStab.top_n_retention[10] != null
                      ? `${(lastStab.top_n_retention[10]! * 100).toFixed(0)}%`
                      : "—"
                  }
                  hint="Fraction of last period's top 10 still in top 10"
                  tone={
                    (lastStab.top_n_retention[10] ?? 0) >= 0.7
                      ? "good"
                      : (lastStab.top_n_retention[10] ?? 0) >= 0.5
                        ? "warn"
                        : "bad"
                  }
                />
                <SparkStat
                  label="Mean rank churn"
                  value={lastStab.mean_rank_churn?.toFixed(1) ?? "—"}
                  hint="Average absolute rank change — lower is more stable"
                  tone="muted"
                />
              </div>
            )}
          </>
        )}
      </Panel>

      {/* Recent runs table */}
      {runs.isLoading && <LoadingState message="Loading backtest history…" />}
      {runs.data && runs.data.length > 0 && (
        <Panel title="Recent runs">
          <DataTable
            dense
            rows={runs.data}
            rowKey={(r) => r.id}
            columns={[
              {
                key: "started",
                header: "Run date",
                cell: (r) => fmtDateTime(r.started_at),
              },
              {
                key: "status",
                header: "Status",
                cell: (r) => <StatusChip status={r.status} />,
              },
              {
                key: "periods",
                header: "Periods",
                align: "right",
                cell: (r) => r.output_summary?.n_periods ?? "—",
              },
              {
                key: "window",
                header: "Window",
                cell: (r) =>
                  r.output_summary
                    ? `${r.output_summary.reference_as_of} → ${r.output_summary.current_as_of}`
                    : "—",
              },
              {
                key: "spearman",
                header: "Last ρ",
                align: "right",
                cell: (r) => {
                  const stabs = r.output_summary?.backtest?.stabilities ?? [];
                  const s = stabs[stabs.length - 1];
                  return s?.spearman != null ? s.spearman.toFixed(3) : "—";
                },
              },
              {
                key: "top10",
                header: "Top-10 ret.",
                align: "right",
                cell: (r) => {
                  const stabs = r.output_summary?.backtest?.stabilities ?? [];
                  const s = stabs[stabs.length - 1];
                  const v = s?.top_n_retention[10];
                  return v != null ? `${(v * 100).toFixed(0)}%` : "—";
                },
              },
              {
                key: "churn",
                header: "Mean churn",
                align: "right",
                cell: (r) => {
                  const stabs = r.output_summary?.backtest?.stabilities ?? [];
                  const s = stabs[stabs.length - 1];
                  return s?.mean_rank_churn != null
                    ? s.mean_rank_churn.toFixed(1)
                    : "—";
                },
              },
            ]}
          />
        </Panel>
      )}
      {runs.data && runs.data.length === 0 && (
        <EmptyState
          title="No backtest runs yet"
          description="Click 'Run now' above to trigger your first validation sweep."
        />
      )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// P&L simulation view (G8 / D-1)
// ---------------------------------------------------------------------------
//
// Wires POST /backtest/pnl. Because that endpoint needs ≥2 real scored
// as-of dates, we first fetch the available dates for the active weight
// vector (GET /backtest/available-dates) and run the simulation over all
// of them. Equal-weight long-only top-N portfolio, rebalanced at each
// date, with a transaction-cost drag — decision-support only, never an
// order.

function PnlBacktestView({
  activeWeightVectorId,
}: {
  activeWeightVectorId: number | undefined;
}) {
  const [topN, setTopN] = useState(20);
  const [tcBps, setTcBps] = useState(50);
  const [lookback, setLookback] = useState(365);
  const [report, setReport] = useState<BacktestPnlReport | null>(null);

  const dates = useQuery({
    queryKey: ["backtest-available-dates", activeWeightVectorId, lookback],
    queryFn: () => getBacktestAvailableDates(activeWeightVectorId!, lookback),
    enabled: activeWeightVectorId !== undefined,
  });

  const nDates = dates.data?.length ?? 0;

  const run = useMutation({
    mutationFn: () =>
      runBacktestPnl({
        weight_vector_id: activeWeightVectorId!,
        as_of_dates: dates.data ?? [],
        top_n: topN,
        tc_bps: tcBps,
      }),
    onSuccess: (data) => setReport(data),
  });

  if (!activeWeightVectorId) {
    return (
      <EmptyState
        title="No active weight vector"
        description="Activate a weight vector on the Settings page first."
      />
    );
  }

  return (
    <div className="space-y-4">
      <Panel
        title="P&L simulation"
        description="Equal-weight long-only top-N portfolio, rebalanced at each scored as-of date, net of transaction costs. Hypothetical — decision support only, not an order."
        action={
          <Button
            variant="primary"
            size="sm"
            loading={run.isPending}
            disabled={nDates < 2}
            onClick={() => run.mutate()}
          >
            Run simulation
          </Button>
        }
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="pnl-topn">Top-N portfolio</Label>
            <NumberInput
              id="pnl-topn"
              min={1}
              max={200}
              step={1}
              value={topN}
              onChange={(e) => setTopN(Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pnl-tc">Round-trip TC (bps)</Label>
            <NumberInput
              id="pnl-tc"
              min={0}
              max={500}
              step={5}
              value={tcBps}
              onChange={(e) => setTcBps(Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pnl-lookback">Lookback (days)</Label>
            <Select
              id="pnl-lookback"
              value={lookback}
              onChange={(e) => setLookback(Number(e.target.value))}
            >
              <option value={180}>180 (6m)</option>
              <option value={365}>365 (1y)</option>
              <option value={730}>730 (2y)</option>
            </Select>
          </div>
        </div>

        <p className="mt-2 text-2xs text-foreground-muted">
          {dates.isLoading
            ? "Loading available scored dates…"
            : nDates < 2
              ? `Only ${nDates} scored as-of date${nDates === 1 ? "" : "s"} in the last ${lookback}d — a P&L backtest needs at least 2. Run more daily scoring passes (or widen the lookback) to accumulate history.`
              : `${nDates} scored as-of dates available — the simulation rebalances at each.`}
        </p>
        {run.isError && (
          <p className="mt-2 text-xs text-danger">{(run.error as Error).message}</p>
        )}
      </Panel>

      {report && <PnlResultDisplay report={report} />}
    </div>
  );
}

function PnlResultDisplay({ report }: { report: BacktestPnlReport }) {
  const m = report.metrics;

  // Build a cumulative equity curve from the per-period returns (base 1.0).
  const equity: { date: string; value: number }[] = [];
  let cum = 1;
  for (const p of report.period_pnl) {
    cum *= 1 + p.portfolio_return;
    equity.push({ date: p.to_date, value: cum });
  }

  return (
    <>
      <Panel
        title={`Result — top ${report.top_n}, ${report.tc_bps}bps TC`}
        description={`${m.n_periods} rebalance periods.`}
      >
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <SparkStat
            label="Total return"
            value={fmtPercent(m.total_return)}
            tone={m.total_return > 0 ? "good" : m.total_return < 0 ? "bad" : "muted"}
          />
          <SparkStat
            label="Annualised"
            value={fmtPercent(m.annualised_return)}
            tone={
              m.annualised_return > 0 ? "good" : m.annualised_return < 0 ? "bad" : "muted"
            }
          />
          <SparkStat
            label="Ann. vol"
            value={m.annualised_volatility != null ? fmtPercent(m.annualised_volatility) : "—"}
            tone="muted"
          />
          <SparkStat
            label="Sharpe"
            value={m.sharpe_ratio != null ? fmtNumber(m.sharpe_ratio) : "—"}
            tone={
              m.sharpe_ratio == null
                ? "muted"
                : m.sharpe_ratio >= 1
                  ? "good"
                  : m.sharpe_ratio >= 0.5
                    ? "warn"
                    : "bad"
            }
          />
          <SparkStat
            label="Max drawdown"
            value={fmtPercent(Math.abs(m.max_drawdown))}
            tone={
              Math.abs(m.max_drawdown) > 0.2
                ? "bad"
                : Math.abs(m.max_drawdown) > 0.1
                  ? "warn"
                  : "muted"
            }
          />
          <SparkStat
            label="Calmar"
            value={m.calmar_ratio != null ? fmtNumber(m.calmar_ratio) : "—"}
            tone="muted"
          />
        </div>

        {equity.length >= 2 && (
          <div className="mt-4">
            <PnlEquityCurve points={equity} />
          </div>
        )}
      </Panel>

      <Panel title="Per-period P&L">
        <DataTable
          dense
          rows={report.period_pnl}
          rowKey={(p) => `${p.from_date}-${p.to_date}`}
          columns={[
            { key: "period", header: "Period", cell: (p) => `${p.from_date} → ${p.to_date}` },
            {
              key: "ret",
              header: "Net return",
              align: "right",
              cell: (p) => (
                <span className={p.portfolio_return >= 0 ? "text-success" : "text-danger"}>
                  {fmtPercent(p.portfolio_return)}
                </span>
              ),
            },
            {
              key: "gross",
              header: "Gross",
              align: "right",
              cell: (p) => fmtPercent(p.gross_return),
            },
            {
              key: "tc",
              header: "TC drag",
              align: "right",
              cell: (p) => fmtPercent(p.tc_drag),
            },
            {
              key: "turnover",
              header: "Turnover",
              align: "right",
              cell: (p) => fmtPercent(p.turnover),
            },
            {
              key: "holdings",
              header: "Names",
              align: "right",
              cell: (p) => p.holdings.length,
            },
          ]}
        />
      </Panel>
    </>
  );
}

function PnlEquityCurve({ points }: { points: { date: string; value: number }[] }) {
  return (
    <Plot
      data={[
        {
          type: "scatter",
          mode: "lines",
          x: points.map((p) => p.date),
          y: points.map((p) => p.value),
          line: { color: "#3b82f6", width: 2 },
          fill: "tozeroy",
          fillcolor: "rgba(59,130,246,0.08)",
          hovertemplate: "%{x}: %{y:.3f}×<extra></extra>",
        } as never,
      ]}
      layout={{
        height: 260,
        paper_bgcolor: "transparent",
        plot_bgcolor: "transparent",
        font: { family: "Inter, ui-sans-serif", color: "#9aa3ad", size: 11 },
        margin: { t: 10, r: 16, b: 32, l: 44 },
        xaxis: { gridcolor: "rgba(148,163,184,0.12)" },
        yaxis: {
          gridcolor: "rgba(148,163,184,0.12)",
          title: { text: "Growth of 1.0" },
          tickformat: ".2f",
        },
        showlegend: false,
      }}
      config={PLOTLY_CONFIG_BASE}
      style={{ width: "100%" }}
    />
  );
}

// ---------------------------------------------------------------------------
// Watchdogs (G15)
// ---------------------------------------------------------------------------

function WatchdogsSection({ activeWeightVectorId }: { activeWeightVectorId: number | undefined }) {
  const [results, setResults] = useState<WatchdogResult[] | null>(null);

  const run = useMutation({
    mutationFn: () =>
      runWatchdogs({
        weightVectorIds: activeWeightVectorId ? [activeWeightVectorId] : undefined,
      }),
    onSuccess: (data) => setResults(data),
  });

  return (
    <div className="space-y-4">
      <Panel
        title="Pipeline watchdogs"
        description="Heartbeat (scoring pipeline freshness) · Model agreement (cross-weight-vector rank correlation) · Risk (circuit-breaker sweep)."
        action={
          <Button variant="primary" size="sm" loading={run.isPending} onClick={() => run.mutate()}>
            Run watchdogs
          </Button>
        }
      >
        {run.isError && <ErrorState detail={(run.error as Error).message} />}
        {!results && !run.isPending && (
          <EmptyState title="Click 'Run watchdogs' to execute" description="All three checks run in sequence." />
        )}
        {run.isPending && <LoadingState message="Running watchdogs…" />}
      </Panel>

      {results && results.map((r) => (
        <WatchdogCard key={r.watchdog} result={r} />
      ))}
    </div>
  );
}

function WatchdogCard({ result: r }: { result: WatchdogResult }) {
  const tone =
    r.level === "critical" ? "bad" :
    r.level === "warning" ? "warn" : "good";

  const titles: Record<string, string> = {
    heartbeat: "Heartbeat — scoring pipeline",
    model_agreement: "Model agreement",
    risk: "Risk — circuit-breaker sweep",
  };

  return (
    <Panel title={titles[r.watchdog] ?? r.watchdog}>
      <div className="flex items-start gap-3">
        <Pill tone={tone} className="shrink-0 mt-0.5">{r.level}</Pill>
        <p className="text-sm text-foreground-secondary">{r.message}</p>
      </div>

      {/* Show notable details */}
      {r.watchdog === "heartbeat" && r.details.age_days !== undefined && (
        <p className="mt-2 text-2xs text-foreground-muted">
          Last run: {r.details.last_run_at as string ?? "never"} ·{" "}
          Age: {Number(r.details.age_days).toFixed(1)}d ·{" "}
          Threshold: {r.details.max_age_days as number}d
        </p>
      )}

      {r.watchdog === "model_agreement" && Array.isArray(r.details.pairs) && (
        <div className="mt-3">
          <DataTable
            dense
            rows={(r.details.pairs as Array<{ wv_a: number; wv_b: number; spearman: number | null }>)}
            rowKey={(p) => `${p.wv_a}-${p.wv_b}`}
            columns={[
              { key: "pair", header: "Pair (WV)", cell: (p) => `(${p.wv_a}, ${p.wv_b})` },
              {
                key: "spearman",
                header: "Spearman ρ",
                align: "right",
                cell: (p) => p.spearman !== null ? (
                  <span className={p.spearman < 0.7 ? "text-warning" : "text-success"}>
                    {p.spearman.toFixed(3)}
                  </span>
                ) : "n/a",
              },
            ]}
          />
        </div>
      )}

      {r.watchdog === "risk" && Array.isArray(r.details.findings) && r.details.findings.length > 0 && (
        <div className="mt-3">
          <DataTable
            dense
            rows={(r.details.findings as Array<{ portfolio_id: number; status: string; drawdown: number; message: string }>)}
            rowKey={(f) => f.portfolio_id}
            columns={[
              { key: "pid", header: "Portfolio", cell: (f) => `#${f.portfolio_id}` },
              { key: "status", header: "Status", cell: (f) => <Pill tone={f.status === "HARD_BREACH" ? "bad" : "warn"}>{f.status}</Pill> },
              { key: "dd", header: "Drawdown", align: "right", cell: (f) => fmtPercent(Math.abs(f.drawdown)) },
            ]}
          />
        </div>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// LLM cost tracker (G22)
// ---------------------------------------------------------------------------

function LLMCostsSection() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const costs = useQuery({
    queryKey: ["llm-costs", year, month],
    queryFn: () => getLLMCosts({ year, month }),
  });

  const budget = useQuery({
    queryKey: ["llm-budget"],
    queryFn: () => getLLMBudget(),
  });

  const c = costs.data;
  const b = budget.data;

  return (
    <div className="space-y-4">
      {/* Budget status banner */}
      {b && b.budget_usd > 0 && (
        <Panel title="Monthly budget">
          <div className="flex items-center gap-3">
            <Pill tone={!b.allowed ? "bad" : (b.remaining_usd !== null && b.remaining_usd < b.budget_usd * 0.1) ? "warn" : "good"}>
              {b.allowed ? "Within budget" : "Budget exceeded"}
            </Pill>
            <span className="text-sm text-foreground-secondary">
              ${fmtNumber(b.budget_usd - (b.remaining_usd ?? 0))} spent of ${fmtNumber(b.budget_usd)} cap
              {b.remaining_usd !== null && ` · $${fmtNumber(b.remaining_usd)} remaining`}
            </span>
          </div>
          {b.remaining_usd !== null && (
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-surface-base">
              <div
                className={`h-full rounded-full transition-all ${!b.allowed ? "bg-danger" : "bg-accent"}`}
                style={{ width: `${Math.min(100, ((b.budget_usd - b.remaining_usd) / b.budget_usd) * 100)}%` }}
              />
            </div>
          )}
        </Panel>
      )}

      {/* Month picker + summary */}
      <Panel
        title="LLM usage"
        description="Aggregated from prompt_log table."
        action={
          <div className="flex items-center gap-2">
            <Select value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24">
              {[now.getFullYear() - 1, now.getFullYear()].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Select>
            <Select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="w-24">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString("en-US", { month: "short" })}</option>
              ))}
            </Select>
          </div>
        }
      >
        {costs.isLoading && <LoadingState />}
        {costs.isError && <ErrorState detail={(costs.error as Error).message} />}
        {c && <LLMCostDisplay data={c} />}
      </Panel>
    </div>
  );
}

function LLMCostDisplay({ data: c }: { data: LLMCostSummary }) {
  const modelRows = Object.entries(c.by_model).sort((a, b) => b[1] - a[1]);
  const templateRows = Object.entries(c.by_template).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-md border border-border-subtle bg-surface-subtle p-3">
          <p className="text-2xs uppercase tracking-wider text-foreground-muted">Total cost</p>
          <p className="mt-1 text-xl font-semibold text-foreground-primary">${c.total_cost_usd.toFixed(2)}</p>
        </div>
        <div className="rounded-md border border-border-subtle bg-surface-subtle p-3">
          <p className="text-2xs uppercase tracking-wider text-foreground-muted">Tokens in</p>
          <p className="mt-1 text-xl font-semibold text-foreground-primary">{fmtCompact(c.total_tokens_in)}</p>
        </div>
        <div className="rounded-md border border-border-subtle bg-surface-subtle p-3">
          <p className="text-2xs uppercase tracking-wider text-foreground-muted">Tokens out</p>
          <p className="mt-1 text-xl font-semibold text-foreground-primary">{fmtCompact(c.total_tokens_out)}</p>
        </div>
        <div className="rounded-md border border-border-subtle bg-surface-subtle p-3">
          <p className="text-2xs uppercase tracking-wider text-foreground-muted">Budget used</p>
          <p className={`mt-1 text-xl font-semibold ${c.budget_pct_used !== null && c.budget_pct_used > 0.9 ? "text-danger" : "text-foreground-primary"}`}>
            {c.budget_pct_used !== null ? fmtPercent(c.budget_pct_used) : c.budget_usd > 0 ? "—" : "No cap"}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {modelRows.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium text-foreground-secondary">By model</p>
            <DataTable
              dense
              rows={modelRows}
              rowKey={([m]) => m}
              columns={[
                { key: "model", header: "Model", cell: ([m]) => <code className="text-2xs">{m}</code> },
                { key: "cost", header: "Cost", align: "right", cell: ([, usd]) => `$${usd.toFixed(4)}` },
                {
                  key: "bar",
                  header: "",
                  cell: ([, usd]) => {
                    const max = modelRows[0]?.[1] ?? 1;
                    return (
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-surface-base">
                        <div className="h-full bg-accent/70" style={{ width: `${(usd / max) * 100}%` }} />
                      </div>
                    );
                  },
                },
              ]}
            />
          </div>
        )}

        {templateRows.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium text-foreground-secondary">By template</p>
            <DataTable
              dense
              rows={templateRows}
              rowKey={([t]) => t}
              columns={[
                { key: "tmpl", header: "Template", cell: ([t]) => <code className="text-2xs">{t}</code> },
                { key: "cost", header: "Cost", align: "right", cell: ([, usd]) => `$${usd.toFixed(4)}` },
                {
                  key: "bar",
                  header: "",
                  cell: ([, usd]) => {
                    const max = templateRows[0]?.[1] ?? 1;
                    return (
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-surface-base">
                        <div className="h-full bg-accent/70" style={{ width: `${(usd / max) * 100}%` }} />
                      </div>
                    );
                  },
                },
              ]}
            />
          </div>
        )}

        {modelRows.length === 0 && templateRows.length === 0 && (
          <EmptyState title="No LLM calls this month" description="Costs will appear here once memos are generated." />
        )}
      </div>
    </div>
  );
}

function SparkStat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: "good" | "warn" | "bad" | "muted";
}) {
  const colorCls =
    tone === "good"
      ? "text-success"
      : tone === "warn"
        ? "text-warning"
        : tone === "bad"
          ? "text-danger"
          : "text-foreground-primary";
  return (
    <div className="rounded-md border border-border-subtle bg-surface-subtle p-3">
      <p className="text-2xs uppercase tracking-wider text-foreground-muted">{label}</p>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${colorCls}`}>{value}</p>
      {hint && <p className="mt-0.5 text-2xs text-foreground-muted">{hint}</p>}
    </div>
  );
}

function StatusChip({ status }: { status: string }) {
  const tone =
    status === "succeeded"
      ? "good"
      : status === "running" || status === "pending"
        ? "ok"
        : status === "failed"
          ? "bad"
          : "muted";
  return <Pill tone={tone}>{status}</Pill>;
}
